import clsx from "clsx"
import { FC } from "react"
import styles from "./PrivacyPolicyContent.module.scss"

export const PrivacyPolicyContent: FC = () => {
  return (
    <div className={clsx(styles.PrivacyPolicyContent)}>
      <p className={clsx(styles.PrivacyPolicyContent_lastUpdated)}>
        <i>Last updated on March 17, 2026</i>
      </p>
      <h1>Privacy Policy</h1>

      <h3>1. INTRODUCTION</h3>
      <p>
        This Privacy Policy (the “<b>Policy</b>”) provides a clear and
        comprehensive explanation of how Developers (“<b>Developers</b>”, “
        <b>We</b>”, or “<b>Us</b>”), as the data controller, collects,
        processes, stores and protects Your personal data in the context of
        providing the Card Game Service (“<b>Service</b>”) to Users through the
        Platform, including via the Telegram Bot, Telegram Mini Application, and
        the web version of the Card Game available at{" "}
        <a
          href="https://app.clashofdiggers.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.clashofdiggers.com
        </a>{" "}
        (the “<b>Website</b>”), at a level appropriate to the data protection
        law applicable in the British Virgin Islands, the{" "}
        <a
          href="https://laws.gov.vg/Laws/data-protection-act-2021"
          target="_blank"
          rel="noopener noreferrer"
        >
          Data Protection Act, 2021
        </a>{" "}
        (“<b>DPA</b>”) and relevant regulations.
      </p>
      <p>
        This Policy applies to all individuals who interact with the Developers
        in the context of using Our Services, visiting Our Platform, or
        otherwise engaging with Us in a way that involves the processing of
        personal data.
      </p>
      <p>
        We are committed to ensuring that Your privacy is protected and that
        Your personal data is processed fairly, lawfully, securely, and
        transparently.
      </p>
      <p>
        <i>
          By accessing or using Our Service, You acknowledge that You have read,
          understood, and accepted this Privacy Policy. If you do not agree with
          any of the terms of this Policy, kindly exit this Platform and refrain
          from using Our Services.
        </i>
      </p>
      <p>
        This Privacy Policy is a supplement to the Telegram Privacy Policy and
        the Third-Party Services Privacy Policy as described in Section 6 of the
        Telegram Privacy Policy for Third-Party Services. By using this Telegram
        Mini Application (hereinafter referred to as the “<b>App</b>”) you are
        also agreeing to be bound by Telegram Privacy Policy (
        <a
          href="https://telegram.org/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://telegram.org/privacy/
        </a>
        ) and Third-Party Services Privacy Policy (
        <a
          href="https://telegram.org/privacy-tpa"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://telegram.org/privacy-tpa
        </a>
        ).
      </p>

      <h3>2. DEFINITIONS</h3>
      <ul>
        <li>
          “<b>Data processor</b>” means a person who, processes data on behalf
          of a data controller, but does not include an employee of the data
          controller;
        </li>
        <li>
          “<b>Data Subject</b>” is a natural person, whether living or deceased,
          whose Personal Data is being Processed.
        </li>
        <li>
          <b>“Data controller”</b> means a person who either alone or jointly or
          in common with other persons processes any personal data, or has
          control over, or authorises the processing of any personal data, but
          does not include a data processor. For the purposes of this Policy,
          the Developers act as the Data Controller in relation to the personal
          data collected and processed through the Card Game and related
          Services;
        </li>
        <li>
          <b>“Clash of Diggers”</b> (the <b>“Card Game”</b> or <b>“the Game”</b>
          ) means the digital card-based game operated by the Developers and
          accessible via the Platform, including through a dedicated Telegram
          Bot and the web version available at{" "}
          <a
            href="https://app.clashofdiggers.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://app.clashofdiggers.com
          </a>{" "}
          (the “<b>Website</b>”), in which Users participate in
          player-versus-player matches using digital cards with various
          abilities, and may obtain or purchase certain in-game items such as
          cards, cosmetic skins, emoji packs, or other digital content related
          to gameplay.
        </li>
        <li>
          <b>“Telegram Bot”</b> means a software-based automated tool or script
          that interacts with Users via the Telegram Messenger platform,
          performing predefined tasks, collecting User input, and providing
          services or information as instructed by its developers or
          administrators. The Telegram Bot acts as an interface between the
          Developers and the User within the scope of the Telegram
          infrastructure;
        </li>
        <li>
          <b>“Platform”</b> means the digital environment through which the Card
          Game and Services are made available to Users, including the Telegram
          Bot, the Website, and any related interfaces, applications, or
          integrations through which Users may access the Card Game.
        </li>
        <li>
          <b>“Personal data”</b> means any information, which:
          <ol>
            <li>
              is being processed wholly or partly by means of equipment
              operating automatically in response to instructions given for that
              purpose;
            </li>
            <li>
              is recorded with the intention that it should wholly or partly be
              processed by means of such equipment;
            </li>
            <li>
              is recorded as part of a relevant filing system or with the
              intention.
            </li>
          </ol>
        </li>
        <li>
          <b>“Processing”</b> means collecting, recording, holding or storing
          the personal data or carrying out any operation or set of operations
          on the personal data, including the:
          <ol>
            <li>organisation, adaptation or alteration of personal data;</li>
            <li>retrieval, consultation or use of personal data;</li>
            <li>
              disclosure of personal data by transmission, transfer,
              dissemination or otherwise making available;
            </li>
            <li>
              alignment, combination, correction, erasure or destruction of
              personal data.
            </li>
          </ol>
        </li>
        <li>
          “<b>Parties</b>” means Developers and the User(s) or Players.
        </li>
        <li>
          <b>“Services”</b> means all and any Services provided by the
          Developers through the Platform.
        </li>
        <li>
          <b>“Players” or “User(s)” or “You”</b> means individuals who access or
          participate in the Card Game through the Platform and use the Services
          provided by the Developers.
        </li>
        <li>
          <b>“Information Commissioner”</b> means the person appointed for an
          office established pursuant to DPA, responsible among others for
          monitoring compliance by public and private bodies with the
          requirements of DPA and receiving and investigating complaints about
          alleged violations of the data protection principles and in respect
          thereof, may make reports to complainants.
        </li>
        <li>
          <b>“Sensitive personal data”</b> means any personal data about a data
          subject’s physical or mental health; sexual orientation; political
          opinions; religious beliefs or other beliefs of a similar nature;
          criminal convictions, the commission or alleged commission, of any
          offence; any other personal data that the Minister may by Order
          prescribe.
        </li>
      </ul>

      <h3>3. INFORMATION WE MAY COLLECT</h3>
      <p>
        3.1. We may collect the following types of information that may be
        considered personal data:
      </p>
      <p>3.1.1. Information You Provide Directly.</p>
      <p>
        Telegram Data: When You authorize the App through Telegram, we receive
        Your:
      </p>
      <ul>
        <li>Telegram User ID.</li>
        <li>Public Telegram name.</li>
        <li>Username.</li>
        <li>Profile picture.</li>
      </ul>
      <p>
        3.1.2. This data is used for in-game leaderboards, the correct operation
        of the referral system, and maintaining Your in-game progress.
      </p>
      <p>
        3.2. <b>Wallet Address:</b> If You choose to link Your TON-based
        cryptocurrency wallet to the Services via TON Connect (e.g.,
        MyTonWallet, Tonkeeper, Wallet in Telegram), We collect Your public
        wallet address. This is optional, initiated by You, and not required to
        use the basic functionality of the Services. You can unlink your wallet
        at any time. Wallet addresses are public blockchain identifiers. The
        Developers do not have access to Users’ private keys and do not control
        or manage Users’ cryptocurrency wallets. The Developers do not control,
        operate, or provide cryptocurrency wallet services. Any wallet
        connection is initiated voluntarily by the User through third-party
        wallet providers. Users remain solely responsible for the security of
        their wallets and private keys.
      </p>
      <p>
        3.3. <b>Email Address:</b> If You choose to link Your Email address to
        the Services, we may collect and process it. Linking an Email is
        optional and not required for using the basic functionality of the
        Services. However, it may be necessary for certain enhanced features.
      </p>
      <p>
        3.4. <b>Information Collected Automatically:</b>
      </p>
      <p>
        3.4.1. Initial IP Address: When You access the Services, certain
        technical information such as Your IP address may be temporarily
        processed in order to determine approximate geographic location, ensure
        compliance with applicable restrictions, and maintain system security.
        This information is not retained longer than necessary for these
        purposes.
      </p>
      <p>
        3.4.2. Device ID: A unique identifier assigned to Your device on first
        use, used to save progress before authorization. This identifier is not
        linked to any personal information before authorization.
      </p>
      <p>
        3.5. <b>Generated Data:</b>
      </p>
      <p>
        3.5.1 In-game activity data: We generate and analyze data related to
        Your in-game activity, achievements, and progress within the App. This
        data is used to operate and improve the Services, personalize Your
        experience, and for Our anti-cheat system.
      </p>
      <p>
        3.6. When Users participate in gameplay features of the Card Game, we
        may collect and process information related to gameplay activities,
        including:
      </p>
      <ul>
        <li>participation in player-versus-player matches;</li>
        <li>match results and gameplay statistics;</li>
        <li>records of in-game items obtained or purchased;</li>
        <li>participation in gameplay modes involving token-based stakes;</li>
        <li>
          blockchain transaction identifiers associated with payments or match
          participation.
        </li>
      </ul>
      <p>
        3.7. Certain features of the Card Game may involve interaction with
        blockchain networks, including the TON blockchain.
      </p>
      <p>
        Where Users choose to participate in gameplay modes involving
        cryptocurrency transactions or connect a digital wallet, certain
        transaction-related data may become publicly available on the
        blockchain. Such information may include:
      </p>
      <ul>
        <li>wallet addresses;</li>
        <li>transaction identifiers (transaction hashes);</li>
        <li>timestamps of blockchain transactions.</li>
      </ul>
      <p>
        Blockchain networks operate on decentralized infrastructure and are not
        controlled by the Developers. Information recorded on the blockchain may
        be publicly accessible and cannot be modified or deleted by the
        Developers.
      </p>

      <h3>4. HOW WE MAY USE YOUR INFORMATION</h3>
      <p>
        4.1. We may use the information we gather for several purposes,
        including but not limited to:
      </p>
      <ul>
        <li>
          <b>Operating and Supporting the Game:</b> To deliver core
          functionalities and ensure smooth performance of the App.
        </li>
        <li>
          <b>Enhancing User Experience:</b> To evaluate user interaction with
          the Game and implement improvements to its functionality and
          usability.
        </li>
        <li>
          <b>User Communication:</b> To address Your requests, offer customer
          support, and keep You informed about updates or changes related to the
          Game.
        </li>
        <li>
          <b>Game Security and Integrity:</b> To monitor, identify, and prevent
          potential misuse, technical issues, or breaches of the Game’s
          functionality.
        </li>
        <li>
          <b>Marketing and Targeted Advertising:</b> To display or deliver
          promotional content, including targeted advertisements based on Your
          device type (e.g., iOS or Android), platform activity, or other
          technical identifiers. We may use non-personally identifiable
          information to tailor the advertising experience and evaluate the
          performance of marketing campaigns. No personally identifiable data
          (such as name or Email) will be shared with third parties for this
          purpose unless You have provided explicit consent.
        </li>
        <li>
          <b>Fair Gameplay and Fraud Prevention</b>: to detect cheating,
          multi-accounting, manipulation of match outcomes, abuse of token-based
          gameplay modes, or other violations of the Terms of Use.
        </li>
        <li>
          <b>Compliance with Legal and Regulatory Obligations</b>: to verify
          geographic restrictions, enforce sanctions compliance, and prevent
          access to the Services from jurisdictions where such Services may be
          restricted or prohibited.
        </li>
        <li>
          <b>AML and Fraud Monitoring</b>: to monitor transactions and gameplay
          activity where necessary to prevent fraud, money laundering, sanctions
          violations, or abuse of token-based gameplay features.
        </li>
      </ul>

      <h3>5. DATA SECURITY</h3>
      <p>
        5.1. We take the protection of Your personal data seriously. We
        implement appropriate technical and organizational measures to safeguard
        Your information against accidental or unlawful destruction, loss,
        alteration, unauthorized disclosure, or access.
      </p>
      <p>
        5.2. While We are committed to maintaining a high standard of data
        security and follow widely accepted industry practices, it is important
        to understand that no method of electronic transmission or storage can
        be guaranteed as entirely secure. Therefore, although We strive to
        protect Your data to the best of our abilities, We cannot guarantee
        absolute security.
      </p>

      <h3>6. CHILDREN’S PRIVACY</h3>
      <p>
        6.1. The protection of children’s personal data is of paramount
        importance to Us.
      </p>
      <p>
        6.2. We do not knowingly collect or process personal data from
        individuals under the age of 18. By using our Services, Users confirm
        that they are at least 18 years of age.
      </p>
      <p>
        6.3. Parents and legal guardians who believe that we may have collected
        information from a child under the age of 18 are encouraged to contact
        us immediately using the contact details provided in this Policy.
      </p>
      <p>
        6.4. As a data controller under the DPA, we take all reasonable steps to
        ensure that all personal data is destroyed or permanently deleted if it
        is no longer required for the purpose for which it was to be processed
        in accordance with Section 11(2)(d) of the Act.
      </p>
      <p>
        6.5. We do not offer Services or content that is targeted specifically
        at minors, nor do we use profiling or behavioral targeting aimed at
        Users under the age of 18.
      </p>

      <h3>7. CONTACT US</h3>
      <p>
        7.1. To the extent that any matters related to the processing of
        personal data are not expressly regulated by this Privacy Policy, such
        matters shall be governed by the Privacy Policy applicable to the game
        “Labrador Adventure”, available at:{" "}
        <a
          href="https://t.me/LabradorAdventuresBot"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://t.me/LabradorAdventuresBot
        </a>
        .
      </p>
      <p>
        7.2. If You have any questions about this Policy, please contact Us at:{" "}
        <b>support@labrador.team</b>
      </p>
    </div>
  )
}
